'use client';

import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/700.css';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SessionProvider } from 'next-auth/react';
import { customPalette } from '@/palette';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
const queryClient = new QueryClient();
declare module '@mui/material/styles' {
  interface PaletteColor {
    containedHoverBackground?: string;
    outlinedHoverBackground?: string;
    outlineRestingBorder?: string;
    content?: string;
  }
  interface PaletteOptions {
    customPalette: {
      backgroundSideMenu: string;
      backgroundSideMenuItemLight: string;
      backgroundSideMenuItemDark: string;
      tableBackground: string;
      tableBorder: string;
      outlinedHoverBackground: string;
      backgroundDefault: string;
      backgroundPaper: string;
      outlineBorder: string;
      divider: string;
    };
  }
  interface Palette {
    customPalette: {
      backgroundSideMenu: string;
      backgroundSideMenuItemLight: string;
      backgroundSideMenuItemDark: string;
      tableBackground: string;
      tableBorder: string;
      outlinedHoverBackground: string;
      outlineRestingBorder: string;
      backgroundDefault: string;
      backgroundPaper: string;
      outlineBorder: string;
      divider: string;
    };
  }
  interface BreakpointOverrides {
    custom1K: true;
  }
}
declare module '@mui/material/styles' {
  interface SimplePaletteColorOptions {
    containedHoverBackground?: string;
    outlinedHoverBackground?: string;
    outlineRestingBorder?: string;
    content?: string;
  }
}
type ModeType = 'light' | 'dark';
type ColorModeContextType = {
  mode: ModeType;
  toggleColorMode: () => void;
};
export const ColorModeContext = createContext<ColorModeContextType>({
  mode: 'dark',
  toggleColorMode: () => {}
});
export const useColorMode = () => {
  return useContext(ColorModeContext);
};
export function Providers({
  children
}: {
  children: ReactNode;
}) {
  const [mode, setMode] = useState<ModeType>('dark');
  const toggleColorMode = useCallback(() => {
    const newMode: ModeType = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('evColorMode', newMode);
  }, [mode]);
  useEffect(() => {
    const storedMode = localStorage.getItem('evColorMode') as ModeType | null;
    const hasStoredPreference = typeof storedMode === 'string';
    if (hasStoredPreference) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMode(storedMode);
    }
  }, []);
  const contextValue = {
    mode,
    toggleColorMode
  };
  const {
    light,
    dark
  } = customPalette;
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'light' ? light.palette.primary.main : dark.palette.primary.main,
        dark: mode === 'light' ? light.palette.primary.dark : dark.palette.primary.dark,
        light: mode === 'light' ? light.palette.primary.light : dark.palette.primary.light,
        contrastText: mode === 'light' ? light.palette.primary.contrastText : dark.palette.primary.contrastText,
        containedHoverBackground: mode === 'light' ? light.palette.primary.containedHoverBackground : dark.palette.primary.containedHoverBackground,
        outlinedHoverBackground: mode === 'light' ? light.palette.primary.outlinedHoverBackground : dark.palette.primary.outlinedHoverBackground,
        outlineRestingBorder: mode === 'light' ? light.palette.primary.outlinedHoverBackground : dark.palette.primary.outlinedHoverBackground
      },
      error: {
        main: mode === 'light' ? light.palette.error.main : dark.palette.error.main,
        dark: mode === 'light' ? light.palette.error.dark : dark.palette.error.dark,
        light: mode === 'light' ? light.palette.error.light : dark.palette.error.light,
        contrastText: mode === 'light' ? light.palette.error.contrastText : dark.palette.error.contrastText,
        containedHoverBackground: mode === 'light' ? light.palette.error.containedHoverBackground : dark.palette.error.containedHoverBackground,
        outlinedHoverBackground: mode === 'light' ? light.palette.error.outlinedHoverBackground : dark.palette.error.outlinedHoverBackground,
        outlineRestingBorder: mode === 'light' ? light.palette.error.outlinedHoverBackground : dark.palette.error.outlinedHoverBackground,
        content: mode === 'light' ? light.palette.error.content : dark.palette.error.content
      },
      warning: {
        main: mode === 'light' ? light.palette.warning.main : dark.palette.warning.main,
        dark: mode === 'light' ? light.palette.warning.dark : dark.palette.warning.dark,
        light: mode === 'light' ? light.palette.warning.light : dark.palette.warning.light,
        contrastText: mode === 'light' ? light.palette.warning.contrastText : dark.palette.warning.contrastText,
        containedHoverBackground: mode === 'light' ? light.palette.warning.containedHoverBackground : dark.palette.warning.containedHoverBackground,
        outlinedHoverBackground: mode === 'light' ? light.palette.warning.outlinedHoverBackground : dark.palette.warning.outlinedHoverBackground,
        outlineRestingBorder: mode === 'light' ? light.palette.warning.outlinedHoverBackground : dark.palette.warning.outlinedHoverBackground,
        content: mode === 'light' ? light.palette.warning.content : dark.palette.warning.content
      },
      success: {
        main: mode === 'light' ? light.palette.success.main : dark.palette.success.main,
        dark: mode === 'light' ? light.palette.success.dark : dark.palette.success.dark,
        light: mode === 'light' ? light.palette.success.light : dark.palette.success.light,
        contrastText: mode === 'light' ? light.palette.success.contrastText : dark.palette.success.contrastText,
        containedHoverBackground: mode === 'light' ? light.palette.success.containedHoverBackground : dark.palette.success.containedHoverBackground,
        outlinedHoverBackground: mode === 'light' ? light.palette.success.outlinedHoverBackground : dark.palette.success.outlinedHoverBackground,
        outlineRestingBorder: mode === 'light' ? light.palette.success.outlinedHoverBackground : dark.palette.success.outlinedHoverBackground,
        content: mode === 'light' ? light.palette.success.content : dark.palette.success.content
      },
      info: {
        main: mode === 'light' ? light.palette.info.main : dark.palette.info.main,
        dark: mode === 'light' ? light.palette.info.dark : dark.palette.info.dark,
        light: mode === 'light' ? light.palette.info.light : dark.palette.info.light,
        contrastText: mode === 'light' ? light.palette.info.contrastText : dark.palette.info.contrastText,
        containedHoverBackground: mode === 'light' ? light.palette.info.containedHoverBackground : dark.palette.info.containedHoverBackground,
        outlinedHoverBackground: mode === 'light' ? light.palette.info.outlinedHoverBackground : dark.palette.info.outlinedHoverBackground,
        outlineRestingBorder: mode === 'light' ? light.palette.info.outlinedHoverBackground : dark.palette.info.outlinedHoverBackground,
        content: mode === 'light' ? light.palette.info.content : dark.palette.info.content
      },
      text: {
        primary: mode === 'light' ? light.palette.text.primary : dark.palette.text.primary,
        secondary: mode === 'light' ? light.palette.text.secondary : dark.palette.text.secondary,
        disabled: mode === 'light' ? light.palette.text.disabled : dark.palette.text.disabled
      },
      action: {
        active: mode === 'light' ? light.palette.action.active : dark.palette.action.active,
        hover: mode === 'light' ? light.palette.action.hover : dark.palette.action.hover,
        selected: mode === 'light' ? light.palette.action.selected : dark.palette.action.selected,
        disabled: mode === 'light' ? light.palette.action.disabled : dark.palette.action.disabled,
        disabledBackground: mode === 'light' ? light.palette.action.disabledBackground : dark.palette.action.disabledBackground,
        focus: mode === 'light' ? light.palette.action.focus : dark.palette.action.focus
      },
      divider: mode === 'light' ? light.palette.other.divider : dark.palette.other.divider,
      background: {
        default: mode === 'light' ? light.palette.other.backgroundDefault : dark.palette.other.backgroundDefault,
        paper: mode === 'light' ? light.palette.other.backgroundPaper : dark.palette.other.backgroundPaper
      },
      customPalette: {
        backgroundSideMenu: mode === 'light' ? light.palette.other.backgroundSideMenu : dark.palette.other.backgroundSideMenu,
        backgroundSideMenuItemLight: mode === 'light' ? light.palette.other.backgroundSideMenuItemLight : dark.palette.other.backgroundSideMenuItemLight,
        backgroundSideMenuItemDark: mode === 'light' ? light.palette.other.backgroundSideMenuItemDark : dark.palette.other.backgroundSideMenuItemDark,
        tableBackground: mode === 'light' ? light.palette.other.tableBackground : dark.palette.other.tableBackground,
        tableBorder: mode === 'light' ? light.palette.other.tableBorder : dark.palette.other.tableBorder,
        outlinedHoverBackground: mode === 'light' ? light.palette.primary.outlinedHoverBackground : dark.palette.primary.outlinedHoverBackground,
        backgroundDefault: mode === 'light' ? light.palette.other.backgroundDefault : dark.palette.other.backgroundDefault,
        backgroundPaper: mode === 'light' ? light.palette.other.backgroundPaper : dark.palette.other.backgroundPaper,
        outlineBorder: mode === 'light' ? light.palette.other.outlineBorder : dark.palette.other.outlineBorder,
        divider: mode === 'light' ? light.palette.other.divider : dark.palette.other.divider
      }
    },
    typography: {
      button: {
        textTransform: 'none'
      }
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 650,
        md: 900,
        lg: 1200,
        xl: 1536,
        custom1K: 1000
      }
    }
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [mode]);

  // this makes it possible to check if the dark theme is set in the global css
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);
  return <QueryClientProvider client={queryClient}>
			<ColorModeContext.Provider value={contextValue}>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<SessionProvider>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							{children}
						</LocalizationProvider>
					</SessionProvider>
				</ThemeProvider>
			</ColorModeContext.Provider>
			<ReactQueryDevtools buttonPosition='bottom-left' />
		</QueryClientProvider>;
}